import { CalendarRange, Download, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { CampoFecha } from '@/components/ui/calendario';

import { PRESETS_RANGO, rangoDePreset } from '../lib/calibracion-format';
import type { CalibracionFiltros, DetalleReporteFiltros } from '../calibracion.types';

const SELECT_CLASSES =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 transition-colors ' +
  'focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const INPUT_CLASSES =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 transition-colors ' +
  'focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

interface ReporteFiltrosProps {
  filtros: DetalleReporteFiltros;
  onFiltrosChange: (filtros: DetalleReporteFiltros) => void;
  catalogo: CalibracionFiltros | undefined;
  /** Muestra los filtros de producto y lado (reporte de detalles). */
  conProducto?: boolean;
  onExportar: () => void;
  exportandoDeshabilitado?: boolean;
  onConsultar?: () => void;
  consultando?: boolean;
}

/** Filtros compartidos por los dos reportes de calibración. */
export function ReporteCalibracionFiltros({
  filtros,
  onFiltrosChange,
  catalogo,
  conProducto = false,
  onExportar,
  exportandoDeshabilitado = false,
  onConsultar,
  consultando = false,
}: ReporteFiltrosProps) {
  const cambiar = (parcial: Partial<DetalleReporteFiltros>) =>
    onFiltrosChange({ ...filtros, ...parcial });

  const rangoInvalido = Boolean(
    filtros.fecha_inicio && filtros.fecha_fin && filtros.fecha_fin < filtros.fecha_inicio,
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-[11px] font-medium text-ink-500">
          Fecha inicio
          <CampoFecha
           
            value={filtros.fecha_inicio}
            max={filtros.fecha_fin || undefined}
            onChange={(event) => cambiar({ fecha_inicio: event.target.value })}
            className={INPUT_CLASSES}
           tipo="date" />
        </label>

        <label className="flex flex-col gap-1 text-[11px] font-medium text-ink-500">
          Fecha fin
          <CampoFecha
           
            value={filtros.fecha_fin}
            min={filtros.fecha_inicio || undefined}
            onChange={(event) => cambiar({ fecha_fin: event.target.value })}
            className={INPUT_CLASSES}
           tipo="date" />
        </label>

        <label className="flex min-w-[12rem] flex-col gap-1 text-[11px] font-medium text-ink-500">
          Ejecutor
          <Select
            value={filtros.executor ?? 'all'}
            onChange={(event) => cambiar({ executor: event.target.value })}
            className={SELECT_CLASSES}
          >
            <option value="all">Todos</option>
            {(catalogo?.executors ?? []).map((ejecutor) => (
              <option key={ejecutor.id} value={String(ejecutor.id)}>
                {ejecutor.name}
              </option>
            ))}
          </Select>
        </label>

        {conProducto ? (
          <>
            <label className="flex min-w-[11rem] flex-col gap-1 text-[11px] font-medium text-ink-500">
              Producto
              <Select
                value={filtros.producto ?? 'all'}
                onChange={(event) => cambiar({ producto: event.target.value })}
                className={SELECT_CLASSES}
              >
                <option value="all">Todos</option>
                {(catalogo?.products ?? []).map((producto) => (
                  <option key={producto.id} value={String(producto.id)}>
                    {producto.producto_nombre ?? producto.name}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex min-w-[9rem] flex-col gap-1 text-[11px] font-medium text-ink-500">
              Lado
              <Select
                value={filtros.bomba ?? 'all'}
                onChange={(event) => cambiar({ bomba: event.target.value })}
                className={SELECT_CLASSES}
              >
                <option value="all">Todos</option>
                {(catalogo?.pumps ?? []).map((lado) => (
                  <option key={lado} value={String(lado)}>
                    Lado {lado}
                  </option>
                ))}
              </Select>
            </label>
          </>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 pb-0.5">
          {onConsultar ? (
            <Button
              size="sm"
              variant="outline"
              leftIcon={<Search className="size-3.5" aria-hidden="true" />}
              onClick={onConsultar}
              loading={consultando}
            >
              Consultar
            </Button>
          ) : null}

          <Button
            size="sm"
            variant="outline"
            leftIcon={<Download className="size-3.5" aria-hidden="true" />}
            onClick={onExportar}
            disabled={exportandoDeshabilitado}
          >
            Exportar
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <CalendarRange className="size-3.5 text-ink-400" aria-hidden="true" />
        {PRESETS_RANGO.map((preset) => (
          <button
            key={preset.id}
            type="button"
            onClick={() => cambiar(rangoDePreset(preset.id))}
            className="rounded-full border border-ink-200 bg-white px-2.5 py-1 text-[11px] font-medium text-ink-600 transition-colors hover:border-brand-300 hover:text-brand-700"
          >
            {preset.label}
          </button>
        ))}

        {rangoInvalido ? (
          <span className="text-[11px] font-medium text-rose-600">
            La fecha de fin no puede ser anterior a la de inicio.
          </span>
        ) : null}
      </div>
    </div>
  );
}
